import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { sessionService } from 'services/sessionService';
import { CompleteSessionDto, CreateSessionDto, EstadoSesion, ReasonSessionDto, RegisterSessionChargeDto, RegisterSessionChargeResponse, Session, SessionListResponse, UpdateSessionDto } from 'types/models';

const KEY = 'sessions';

export function useSessionsByPlan(patientId: string, episodeId: string, planId: string) {
  return useQuery<SessionListResponse, Error, Session[]>({
    queryKey: [KEY, 'plan', planId],
    queryFn: () => sessionService.listByPlan(patientId, episodeId, planId),
    enabled: !!patientId && !!episodeId && !!planId,
    select: (res) => res.data,
  });
}

export function useSessionDetail(
  patientId: string,
  episodeId: string,
  planId: string,
  sessionId: string | null,
) {
  return useQuery({
    queryKey: [KEY, 'detail', sessionId],
    queryFn: () => sessionService.getById(patientId, episodeId, planId, sessionId!),
    enabled: !!patientId && !!episodeId && !!planId && !!sessionId,
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      planId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      planId: string;
      payload: CreateSessionDto;
    }) => sessionService.create(patientId, episodeId, planId, payload),
    onSuccess: (session: Session) => {
      if (session.planId) {
        queryClient.invalidateQueries({ queryKey: [KEY, 'plan', session.planId] });
      }
      queryClient.invalidateQueries({ queryKey: ['plans', 'episode', session.episodeId] });
    },
  });
}

export function useUpdateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      planId,
      sessionId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      planId: string;
      sessionId: string;
      payload: UpdateSessionDto;
    }) => sessionService.update(patientId, episodeId, planId, sessionId, payload),
    onSuccess: (session: Session) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', session.id] });
      if (session.planId) {
        queryClient.invalidateQueries({ queryKey: [KEY, 'plan', session.planId] });
        queryClient.invalidateQueries({ queryKey: ['plans', 'episode', session.episodeId] });
      }
    },
  });
}

export function useDeleteSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      planId,
      sessionId,
    }: {
      patientId: string;
      episodeId: string;
      planId: string;
      sessionId: string;
    }) => sessionService.delete(patientId, episodeId, planId, sessionId),
    onSuccess: (
      _data: void,
      vars: { patientId: string; episodeId: string; planId: string; sessionId: string },
    ) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'plan', vars.planId] });
    },
  });
}

export function useMarkNoShow() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      patientId, episodeId, planId, sessionId, payload,
    }: {
      patientId: string; episodeId: string; planId: string; sessionId: string; payload: ReasonSessionDto;
    }) => sessionService.markNoShow(patientId, episodeId, planId, sessionId, payload),
    onSuccess: (session: Session) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'plan', session.planId] });
      queryClient.invalidateQueries({ queryKey: ['plans', 'episode', session.episodeId] });
      toast({
        title: 'Inasistencia registrada',
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast({
        title: 'Error al registrar inasistencia',
        description: Array.isArray(msg) ? msg.join(' · ') : (msg ?? 'Ocurrió un error inesperado.'),
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}

export function useCancelSession() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      patientId, episodeId, planId, sessionId, payload,
    }: {
      patientId: string; episodeId: string; planId: string; sessionId: string; payload: ReasonSessionDto;
    }) => sessionService.cancelSession(patientId, episodeId, planId, sessionId, payload),
    onSuccess: (session: Session) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'plan', session.planId] });
      queryClient.invalidateQueries({ queryKey: ['plans', 'episode', session.episodeId] });
      toast({
        title: 'Sesión cancelada',
        status: 'info',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast({
        title: 'Error al cancelar sesión',
        description: Array.isArray(msg) ? msg.join(' · ') : (msg ?? 'Ocurrió un error inesperado.'),
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}

export function useRegisterSessionCharge() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      patientId, episodeId, planId, sessionId, payload,
    }: {
      patientId: string; episodeId: string; planId: string; sessionId: string; payload: RegisterSessionChargeDto;
    }) => sessionService.registerCharge(patientId, episodeId, planId, sessionId, payload),
    onSuccess: (res: RegisterSessionChargeResponse) => {
      const { session, billing } = res;
      queryClient.invalidateQueries({ queryKey: ['sessions', 'plan', session.planId] });
      queryClient.invalidateQueries({ queryKey: ['plans', 'episode', session.episodeId] });
      queryClient.invalidateQueries({ queryKey: ['billing-account', session.pacienteId] });
      const isDeferred = session.estado === EstadoSesion.ATENDIDA;
      toast({
        title: isDeferred ? 'Cargo registrado' : 'Atención completada',
        description: isDeferred
          ? `$${billing.totalAmount.toFixed(2)} cobrado. Nota SOAP pendiente.`
          : `$${billing.totalAmount.toFixed(2)} cobrado. Sesión marcada como completada.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast({
        title: 'Error al registrar cargo',
        description: Array.isArray(msg) ? msg.join(' · ') : (msg ?? 'Ocurrió un error inesperado.'),
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}

export function useCompleteSession() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      planId,
      sessionId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      planId: string;
      sessionId: string;
      payload: CompleteSessionDto;
    }) => sessionService.completeSession(patientId, episodeId, planId, sessionId, payload),
    onSuccess: (session: Session) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'plan', session.planId] });
      queryClient.invalidateQueries({ queryKey: ['plans', 'episode', session.episodeId] });
      toast({
        title: 'Sesión completada',
        description: 'La nota SOAP se guardó y se generó el cargo en facturación.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast({
        title: 'Error al completar sesión',
        description: Array.isArray(msg) ? msg.join(' · ') : (msg ?? 'Ocurrió un error inesperado.'),
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}
